import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { QuickstartTheme } from '../types/theme';

interface LoadingBarProps {
  /** Progress from 0 to 100 (optional - if not provided, shows indeterminate animation) */
  progress?: number;
  /** Theme for colors (optional - uses defaults if not provided) */
  theme?: QuickstartTheme;
  /** Width of the loading bar container */
  width?: string | number;
  /** Height of the loading bar in pixels (default: 12px from Figma) */
  height?: number;
  /** Whether to show the circular indicator on the left */
  showIndicator?: boolean;
  /** Custom primary color (overrides theme) */
  primaryColor?: string;
  /** Custom secondary color (overrides theme) */
  secondaryColor?: string;
  /** Custom background color */
  backgroundColor?: string;
  /** Animation duration in seconds for indeterminate mode */
  animationDuration?: number;
}

/**
 * LoadingBar Component
 *
 * A stylized loading bar with gradient fill based on the Figma design.
 * Supports both determinate (progress-based) and indeterminate (animated) modes.
 *
 * Features:
 * - Gradient fill from primary (yellow) to secondary (purple)
 * - Asymmetric border radius (25px left, 10px right)
 * - Optional circular indicator
 * - Smooth animations
 * - Theme-aware colors
 *
 * Usage:
 * ```tsx
 * // Indeterminate (animated)
 * <LoadingBar />
 *
 * // With progress
 * <LoadingBar progress={65} />
 *
 * // With theme
 * <LoadingBar theme={theme} progress={50} />
 * ```
 */
export const LoadingBar = ({
  progress,
  theme,
  width = '100%',
  height = 24,
  showIndicator = false,
  primaryColor,
  secondaryColor,
  backgroundColor,
  animationDuration = 2,
}: LoadingBarProps) => {
  // Default colors from Figma design
  // Gradient: Purple (left) → Yellow (right)
  const primary = primaryColor ?? theme?.primaryColor ?? '#FBBF24'; // Yellow/gold (end color)
  const secondary = secondaryColor ?? '#4338CA'; // Purple/indigo (start color)
  const bgColor = backgroundColor ?? 'rgba(30, 27, 75, 0.9)'; // #1E1B4B at 90%

  // For indeterminate mode, animate the progress with fake progress
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const isIndeterminate = progress === undefined;

  useEffect(() => {
    if (!isIndeterminate) return;

    // Fake progress animation:
    // - Starts fast, slows down as it approaches the target
    // - Never reaches 100% (holds at ~85%)
    // - Gives illusion of progress without lying about completion
    const maxProgress = 85;

    const interval = setInterval(() => {
      setAnimatedProgress((prev) => {
        if (prev >= maxProgress) {
          return maxProgress; // Hold at max
        }

        // Logarithmic slowdown - fast at start, very slow near end
        const remaining = maxProgress - prev;
        const increment = Math.max(0.1, remaining * 0.02);

        return Math.min(maxProgress, prev + increment);
      });
    }, 50);

    return () => clearInterval(interval);
  }, [isIndeterminate]);

  const displayProgress = isIndeterminate ? animatedProgress : progress;

  // Gradient for the fill: Purple (left) → Yellow (right)
  const gradientStyle = {
    background: `linear-gradient(90deg, ${secondary} 0%, ${primary} 100%)`,
  };

  return (
    <div className='relative flex items-center gap-2.5' style={{ width: typeof width === 'number' ? `${width}px` : width }}>
      {/* Circular Indicator */}
      {showIndicator && (
        <motion.div
          className='relative z-10 flex-shrink-0'
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div
            className='rounded-full shadow-lg'
            style={{
              ...gradientStyle,
              width: `${height * 1.5}px`,
              height: `${height * 1.5}px`,
            }}
          />
          {/* Pulse effect */}
          <motion.div
            className='absolute inset-0 rounded-full'
            style={gradientStyle}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </motion.div>
      )}

      {/* Loading Bar Container */}
      <div
        className='relative flex-1 overflow-hidden'
        style={{
          height: `${height}px`,
          backgroundColor: bgColor,
          // From Figma: border-radius: 25px 10px (asymmetric)
          // Scales with height for proper proportions
          borderRadius: `${Math.round(height * 0.55)}px ${Math.round(height * 0.22)}px`,
        }}
      >
        {/* Progress Fill */}
        <motion.div
          className='absolute inset-y-0 left-0 h-full'
          style={{
            ...gradientStyle,
            borderRadius: `${Math.round(height * 0.55)}px ${Math.round(height * 0.22)}px`,
          }}
          initial={{ width: '0%' }}
          animate={{ width: `${displayProgress}%` }}
          transition={{
            duration: isIndeterminate ? 0.1 : 0.3,
            ease: 'easeOut',
          }}
        />

        {/* Shimmer effect for indeterminate mode */}
        {isIndeterminate && (
          <motion.div
            className='absolute inset-y-0 h-full w-32'
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
              borderRadius: `${Math.round(height * 0.55)}px ${Math.round(height * 0.22)}px`,
            }}
            animate={{
              x: ['-100%', '500%'],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Compact version without indicator - just the bar
 */
export const LoadingBarCompact = (props: Omit<LoadingBarProps, 'showIndicator'>) => {
  return <LoadingBar {...props} showIndicator={false} />;
};

/**
 * Mini loading bar for inline use
 */
export const LoadingBarMini = ({
  progress,
  theme,
  width = 200,
  primaryColor,
  secondaryColor,
}: Pick<LoadingBarProps, 'progress' | 'theme' | 'width' | 'primaryColor' | 'secondaryColor'>) => {
  const primary = primaryColor ?? theme?.primaryColor ?? '#FBBF24'; // Yellow (end)
  const secondary = secondaryColor ?? '#4338CA'; // Purple (start)
  const bgColor = 'rgba(30, 27, 75, 0.9)';

  const displayProgress = progress ?? 50;

  return (
    <div
      className='relative h-2 overflow-hidden rounded-full'
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        backgroundColor: bgColor,
      }}
    >
      <motion.div
        className='absolute inset-y-0 left-0 h-full rounded-full'
        style={{
          // Gradient: Purple (left) → Yellow (right)
          background: `linear-gradient(90deg, ${secondary} 0%, ${primary} 100%)`,
        }}
        initial={{ width: '0%' }}
        animate={{ width: `${displayProgress}%` }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      />
    </div>
  );
};

export default LoadingBar;

